// Readiness page view model (2026-10-08). Pure: every figure comes from the
// readiness `Result` + active target the backend returns; this module only
// selects, orders and words them. It never re-derives a threshold, a
// segment, or a score — those are the engine's.

import type {
  ReadinessFactorID,
  ReadinessGoal,
  ReadinessResult,
  ReadinessSegmentGate,
  ReadinessTimeline,
} from "../readiness-types";
import {
  READINESS_FACTOR_LABELS,
  buildFactorBreakdown,
  parseHorizonMonths,
  type FactorBreakdown,
} from "./breakdown";

// ── Score moves ─────────────────────────────────────────────────────────

export interface ScoreMoveStep {
  goal: ReadinessGoal;
  delta: number;
  /** Running score after this move, capped at 100. */
  scoreAfter: number;
}

export interface ScoreMoves {
  start: number;
  steps: ScoreMoveStep[];
  end: number;
}

const MAX_STAIR_STEPS = 3;

/** Points-moving goals only (`raise`/legacy ""), in the engine's order
 *  (already sorted by projected_delta). Lever goals mirror a sibling's
 *  delta and coaching/set_plan carry none, so including them would
 *  double-count or promise points that don't exist. Null when the score
 *  is capped (DNQ) — there is no number to climb from. */
export function buildScoreMoves(result: ReadinessResult): ScoreMoves | null {
  if (result.score === null) return null;
  let running = result.score;
  const steps: ScoreMoveStep[] = [];
  for (const goal of result.goals ?? []) {
    if (steps.length >= MAX_STAIR_STEPS) break;
    if (!isPointsGoal(goal)) continue;
    running = Math.min(100, running + goal.projected_delta);
    steps.push({ goal, delta: goal.projected_delta, scoreAfter: running });
  }
  return { start: result.score, steps, end: running };
}

export function isPointsGoal(goal: ReadinessGoal): boolean {
  return (
    (goal.kind === undefined || goal.kind === "" || goal.kind === "raise") &&
    goal.projected_delta > 0
  );
}

// ── Ready-to-act gates ──────────────────────────────────────────────────

export interface GateRow {
  id: ReadinessSegmentGate["gate"];
  met: boolean;
  label: string;
  detail: string;
}

const TIMELINE_LABELS: Record<ReadinessTimeline, string> = {
  "0_3_months": "0–3 months",
  "3_6_months": "3–6 months",
  "6_12_months": "6–12 months",
  "12_plus": "12+ months",
};

/** One sentence per gate. Thresholds in the label come from the gate's
 *  own `required` value — never a literal here. Null when the backend
 *  didn't report gates (older API / snapshot) so the card can hide
 *  rather than show an empty checklist. */
/** `targetPrice` (the active target) words the DTI detail: the engine's DTI
 *  is the projected back-end ratio at that price, not today's debts alone. */
export function buildGateRows(
  result: ReadinessResult,
  targetPrice?: number | null,
): GateRow[] | null {
  const gates = result.segment_gates;
  if (!gates || gates.length === 0) return null;
  return gates.map((gate) => ({
    id: gate.gate,
    met: gate.met,
    label: gateLabel(gate),
    detail: gateDetail(gate, targetPrice),
  }));
}

function gateLabel(gate: ReadinessSegmentGate): string {
  switch (gate.gate) {
    case "credit":
      return `Credit ${formatNumber(gate.required)} or higher`;
    case "dti":
      return `Debt-to-income under ${formatPercent(gate.required)}`;
    case "cash_to_close":
      return "Cash to close covered";
    case "timeline":
      return gate.required_timeline
        ? `Buying within ${TIMELINE_LABELS[gate.required_timeline]}`
        : "Buying soon";
  }
}

function gateDetail(gate: ReadinessSegmentGate, targetPrice?: number | null): string {
  if (gate.gate === "timeline") {
    return gate.current_timeline
      ? `You said ${TIMELINE_LABELS[gate.current_timeline]}`
      : "Not set";
  }
  if (gate.current === null || gate.current === undefined) return "Not recorded";
  switch (gate.gate) {
    case "credit":
      return formatNumber(gate.current);
    case "dti":
      return targetPrice
        ? `${formatPercent(gate.current)} at ${formatCompactDollars(targetPrice)}`
        : formatPercent(gate.current);
    case "cash_to_close":
      return `${formatCompactDollars(gate.current)} of ${formatCompactDollars(gate.required)}`;
  }
}

// ── Timeline ────────────────────────────────────────────────────────────

/** The buyer's declared purchase window, in months from today. */
const TIMELINE_WINDOW: Record<ReadinessTimeline, [number, number]> = {
  "0_3_months": [0, 3],
  "3_6_months": [3, 6],
  "6_12_months": [6, 12],
  "12_plus": [12, 15],
};

export interface TimelineMonth {
  label: string;
  isToday: boolean;
  inWindow: boolean;
  events: string[];
}

export function buildTimeline(
  result: ReadinessResult,
  timeline: ReadinessTimeline | null | undefined,
  now: Date = new Date(),
): { windowLabel: string | null; months: TimelineMonth[]; hasEvents: boolean } {
  const window = timeline ? TIMELINE_WINDOW[timeline] : null;
  const unlocks = (result.time_unlocks ?? [])
    .map((u) => ({ unlock: u, months: parseHorizonMonths(u.unlock_at) }))
    .filter((u): u is { unlock: typeof u.unlock; months: number } => u.months !== null);
  const lastMonth = Math.max(
    window ? window[1] : 3,
    ...unlocks.map((u) => u.months),
  );
  const count = Math.min(lastMonth, 15) + 1;

  const months: TimelineMonth[] = Array.from({ length: count }, (_, i) => {
    const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
    return {
      label: date.toLocaleString("en-US", { month: "short" }),
      isToday: i === 0,
      inWindow: window ? i >= window[0] && i <= window[1] && i > 0 : false,
      events: unlocks
        .filter((u) => u.months === i)
        .map(({ unlock }) =>
          unlock.projected_delta > 0
            ? `${READINESS_FACTOR_LABELS[unlock.factor]} +${unlock.projected_delta}`
            : READINESS_FACTOR_LABELS[unlock.factor],
        ),
    };
  });

  return {
    windowLabel: timeline ? `Buying in ${TIMELINE_LABELS[timeline]}` : null,
    months,
    // Without a dated event the rail only restates the declared window,
    // which the timeline gate already says — callers hide it then.
    hasEvents: months.some((m) => m.events.length > 0),
  };
}

// ── Factors ─────────────────────────────────────────────────────────────

export interface FactorRowModel extends FactorBreakdown {
  /** 1-based move number when a points goal targets this factor. */
  moveNumber: number | null;
  status: "move" | "strong" | "grow" | "unknown";
}

export function buildFactorRows(
  result: ReadinessResult,
  moves: ScoreMoves | null,
): FactorRowModel[] {
  const moveByFactor = new Map<ReadinessFactorID, number>();
  moves?.steps.forEach((step, i) => {
    if (!moveByFactor.has(step.goal.factor)) moveByFactor.set(step.goal.factor, i + 1);
  });
  return buildFactorBreakdown(result, result.score === null).map((row) => {
    const moveNumber = moveByFactor.get(row.id) ?? null;
    const status: FactorRowModel["status"] = row.isUnknown
      ? "unknown"
      : moveNumber !== null
        ? "move"
        : row.ratio >= 0.75
          ? "strong"
          : "grow";
    return { ...row, moveNumber, status };
  });
}

// ── Hero sentence ───────────────────────────────────────────────────────

export function buildHeroSentence(
  moves: ScoreMoves | null,
  gates: GateRow[] | null,
): string {
  const unmet = gates?.filter((g) => !g.met) ?? [];
  if (gates && unmet.length === 0) return "You meet every Ready to act check.";
  const parts: string[] = [];
  if (moves && moves.steps.length > 0) {
    const added = moves.end - moves.start;
    parts.push(
      `${moves.steps.length === 1 ? "One move adds" : `${moves.steps.length} moves add`} about ${added} points.`,
    );
  }
  if (unmet.length > 0) {
    parts.push(
      `Ready to act still needs: ${unmet.map((g) => lowerFirst(g.label)).join(", ")}.`,
    );
  }
  return parts.join(" ") || "Keep your inputs current and your score stays accurate.";
}

// ── Capped-score notice ─────────────────────────────────────────────────

export interface CapNotice {
  title: string;
  body: string;
}

/** Explains a capped (null) score in buyer language. `cap_reason_detail` is
 *  the engine's short label (`dnqDetail` / `planUnderfundedDetail` in
 *  readiness/plan_viability.go); this only words it. Figures come from the
 *  same result (the DTI gate's `current`) and the active target price —
 *  the DTI the engine gates on is the projected back-end DTI AT that
 *  price, so naming the price is what makes the number make sense. An
 *  unrecognised label falls through verbatim. */
export function buildCapNotice(
  result: ReadinessResult,
  targetPrice?: number | null,
): CapNotice | null {
  if (result.score !== null) return null;
  const detail = result.cap_reason_detail ?? "";
  const atTarget = targetPrice ? ` at your ${formatCompactDollars(targetPrice)} target` : "";

  if (result.cap_reason === "plan_underfunded") {
    return {
      title: "Your plan needs more cash",
      body: `${detail || "Your savings don't cover this plan yet."} Saving more, a smaller down payment, or a lower price closes the gap.`,
    };
  }
  switch (detail) {
    case "DTI over 50%": {
      const dti = result.segment_gates?.find((g) => g.gate === "dti")?.current;
      const share = typeof dti === "number" ? `about ${formatPercent(dti)}` : "more than half";
      return {
        title: "Debt-to-income is holding you back",
        body: `With a new mortgage${atTarget}, your monthly debts would take ${share} of your income. Lenders generally stop at 50%. A lower target price, more income, or less debt opens your path.`,
      };
    }
    case "No income recorded":
      return {
        title: "Add your income to unlock your score",
        body: "We can't size a mortgage without it. Add your income on your Passport and your score appears.",
      };
    case "No credit recorded":
      return {
        title: "Add your credit to unlock your score",
        body: "Most loan terms hinge on your credit. Add an estimate on your Passport and your score appears.",
      };
    case "Credit below 620":
      return {
        title: "Credit is holding you back",
        body: "Most loan programs start at a 620 credit score. Raising yours past it unlocks your score.",
      };
    case "Insufficient cash for target":
      return {
        title: "Cash to close is holding you back",
        body: `Your savings don't yet cover the down payment and closing costs${atTarget}. Saving more or a lower price closes the gap.`,
      };
    default:
      return {
        title: "Your score is on hold",
        body: detail || "Your score appears once the missing inputs are in.",
      };
  }
}

// ── formatting ──────────────────────────────────────────────────────────

function formatNumber(value: number | undefined): string {
  return value === undefined ? "—" : Math.round(value).toLocaleString("en-US");
}

function formatPercent(value: number | undefined): string {
  return value === undefined ? "—" : `${Math.round(value * 100)}%`;
}

function formatCompactDollars(value: number | undefined): string {
  if (value === undefined) return "—";
  if (Math.abs(value) >= 1_000_000) {
    return `$${(Math.round(value / 100_000) / 10).toLocaleString("en-US")}M`;
  }
  if (Math.abs(value) >= 1000) return `$${Math.round(value / 1000).toLocaleString("en-US")}K`;
  return `$${Math.round(value).toLocaleString("en-US")}`;
}

function lowerFirst(text: string): string {
  return text.charAt(0).toLowerCase() + text.slice(1);
}
